/**
 * @file CheckboxButtonGroup.js
 * Customized version of the CheckboxButtonGroup component in react-hook-form-mui.
 * Adds styling and a11y capabilities to the original version.
 *
 * Original version: https://github.com/dohomi/react-hook-form-mui/blob/master/packages/rhf-mui/src/CheckboxButtonGroup.tsx
 */
import { Checkbox, FormControl, FormControlLabel, FormGroup, FormHelperText, FormLabel, useTheme } from '@mui/material';
import { useController } from 'react-hook-form';
import { TextFieldElement, useFormError } from 'react-hook-form-mui';
import { forwardRef } from 'react';
import { useTransform } from './useTransform';
import { propertyExists } from './utils';
import { Box } from '@mui/system';

const CheckboxButtonGroup = forwardRef(function CheckboxButtonGroup(
  props,
  ref
) {
  const {
    helperText,
    options,
    label,
    name,
    parseError,
    required,
    labelKey = 'label',
    valueKey = 'id',
    maxColumns = 3,
    returnObject,
    disabled,
    readOnly,
    row,
    control,
    checkboxColor,
    rules,
    labelProps,
    transform,
    fullWidth,
    textFieldRequired,
    textFieldInline = false,
    defaultValue = [],
    sx = {},
    ...rest
  } = props;

  const theme = useTheme();
  const errorMsgFn = useFormError();
  const customErrorFn = parseError || errorMsgFn;

  const {
    field,
    fieldState: { error, invalid }
  } = useController({
    name,
    rules: {
      ...(required ? { required: 'This field is required' } : {}),
      ...rules,
      nope: () => 'This is false',
    },
    disabled,
    control,
    defaultValue: defaultValue
  });

  const { value: selectedOptions, onChange } = useTransform({
    value: field.value,
    onChange: field.onChange,
    transform: {
      input:
        typeof transform?.input === 'function'
          ? transform.input
          : value => {
            return Array.isArray(value) ? value : [];
          },
      output: transform?.output
    }
  });

  const handleChange = option => {
    const optionValue = propertyExists(option, valueKey)
      ? option[valueKey]
      : option;
    const existsAtIndex = selectedOptions.findIndex(selectedOption => {
      const selectedOptionValue = propertyExists(selectedOption, valueKey)
        ? selectedOption[valueKey]
        : selectedOption;
      return optionValue === selectedOptionValue;
    });

    const newValues = (existsAtIndex === -1
        ? [...selectedOptions, option]
        : selectedOptions.filter((_, index) => existsAtIndex !== index)
    ).map(selectedOption =>
      returnObject || !propertyExists(selectedOption, valueKey)
        ? selectedOption
        : selectedOption[valueKey]
    );
    onChange(newValues);
    if (typeof rest.onChange === 'function') {
      rest.onChange(newValues);
    }
  };

  const renderHelperText = error
    ? typeof customErrorFn === 'function'
      ? customErrorFn(error)
      : error.message
    : helperText;

  let sxOptions = {
    width: '100%',
  };
  if (maxColumns >= 2 && options.length > 3) {
    sxOptions[theme.breakpoints.up('md')] = {
      width: '48%',
    };
  }
  if (options.length > 6 && maxColumns >= 3) {
    sxOptions[theme.breakpoints.up('lg')] = {
      width: '32%',
    };
  }

  return (
    <FormControl
      error={invalid}
      required={required}
      ref={ref}
      component="fieldset"
      fullWidth={!!fullWidth}
      transform={{
        input: (v) => (v == null ? '' : String(v)),
        output: (e) => Number(e.target.value),
      }}
      sx={{
        ...(!row ? {
          '& .MuiFormGroup-root': {
            flexDirection: 'row',
            paddingRight: 0,
            [theme.breakpoints.up('xl')]: {
              // paddingRight: '20%',
            },
            maxWidth: options.length > 6 ? 'min(1200px, 100%)' : 'max(600px, 55%)',
            '& > .MuiFormControlLabel-root': sxOptions,
          }
        } : {}),
        ...sx,
      }}
    >
      {label ? <FormLabel component="legend" sx={{ display: 'flex' }}>{label}</FormLabel> : null}
      <FormGroup row={row} sx={{ alignItems: 'flex-start', justifyContent: 'space-between' }}>
        {options.map(option => {
          const optionValue = propertyExists(option, valueKey)
            ? option[valueKey]
            : option;
          const optionLabel = propertyExists(option, labelKey)
            ? option[labelKey]
            : option;

          const isActive = propertyExists(option, 'active') ? option.active ? 1 : 0 : null;

          const isChecked = selectedOptions.some(selectedOption => {
            const selectedOptionValue = propertyExists(selectedOption, valueKey)
              ? selectedOption[valueKey]
              : selectedOption;
            return selectedOptionValue === optionValue;
          });

          const textField = option?.text ?
            <FormControlLabel
              label={null}
              sx={{ ...(textFieldInline ? {
                  flexGrow: 1,
                  marginLeft: '8px',
                  marginRight: 0,
                  width: '100%',
                } : {
                  width: '100%',
                  marginRight: 0,
                  marginLeft: 0,
                  '& > .MuiStack-root': {
                    marginTop: '-24px',
                    paddingBottom: '8px',
                  },
                }),
                'label.MuiInputLabel-shrink + .MuiOutlinedInput-root fieldset.MuiOutlinedInput-notchedOutline': {
                  legend: {
                    width: 0,
                  },
                },
                '.MuiInputLabel-outlined': {
                  fontWeight: 'normal',
                },
                '.MuiFormControlLabel-asterisk': {
                  display: 'none'
                },
                '.Mui-focused.MuiOutlinedInput-notchedOutline': {
                  borderWidth: '1px',
                },
              }}
              control={<TextFieldElement
                name={option.textId}
                className="other-text-input"
                control={control}
                disabled={disabled}
                required={textFieldRequired}
                fullWidth={!textFieldInline}
                sx={{
                  margin: textFieldInline ? '-5px 0 0' : '',
                  '.MuiInputLabel-shrink': {
                    display: 'none',
                  },
                  '.MuiInputLabel-root': {
                    transform: 'translate(6px, 7px) scale(1)',
                  },
                  '.MuiInputBase-root .MuiInputBase-input': {
                    padding: '6px 7px',
                  },
                }}
                label={option?.textFieldLabel}
              />}
            /> : false;

          const fieldId = `${name}.${String(optionValue).replace(' ', '-')}`;
          return (
            <FormControlLabel
              {...labelProps}
              htmlFor={fieldId}
              sx={{
                ...(textField ? {
                  alignItems: 'flex-start',
                  '& .MuiFormControlLabel-label': {
                    flexGrow: 1,
                    paddingTop: '9px',
                  },
                  '& .Mui-error.MuiFormHelperText-root': {
                    position: 'absolute',
                    bottom: '-20px',
                  },
                } : {}),
                ...(isActive === 0 && {
                  '.MuiFormControlLabel-label': {
                    opacity: 0.5,
                  },
                }),
              }}
              control={
                <Checkbox
                  id={fieldId}
                  sx={{
                    color: error ? theme.palette.error.main : undefined,
                  }}
                  color={checkboxColor}
                  value={optionValue}
                  checked={isChecked}
                  disabled={disabled || readOnly}
                  inputProps={{
                    disabled: (readOnly ? false : disabled),
                  }}
                  onChange={() => readOnly ? null : handleChange(option)}
                />
              }
              label={
                textFieldInline ? (
                  <Box sx={{ display: 'inline-flex', width: '100%' }}>
                    <span>{optionLabel}{option?.text}</span>
                    { isChecked && textField }
                  </Box>
                ) : (
                  <>
                    <span>{optionLabel}{option?.text}</span>
                    { isChecked && textField }
                  </>
                )
              }
              key={`${optionValue}`}
            />
          );
        })}
      </FormGroup>
      {renderHelperText && <FormHelperText>{renderHelperText}</FormHelperText>}
    </FormControl>
  );
});
CheckboxButtonGroup.displayName = 'CheckboxButtonGroup';
export default CheckboxButtonGroup;
